import { Form, Modal } from 'antd';
import React, { useCallback, useMemo, useRef } from 'react';
import styles from './CreateNewWorkScheduleByPeriod.module.scss';
import { FormFooter } from './components/FormFooter';
import { MainAddressSection } from './components/MainAddressSection';
import { ServiceSetsSection } from './components/ServiceSetsSection';
import { WorkersSection } from './components/WorkersSection';
import {
  getWorkScheduleByPeriodInitialValues,
  hasAddressValues,
  hasFormValues,
  isWorkScheduleByPeriodFieldEditable,
  shouldUseWorkScheduleByPeriodFieldInitialValue,
} from './CreateNewWorkScheduleByPeriod.helpers';
import { useAddressBlock } from './hooks/useAddressBlock';
import { useScheduleEntities } from './hooks/useScheduleEntities';
import { useWorkSchedulePeriodCheck } from './hooks/useWorkSchedulePeriodCheck';
import {
  workScheduleByPeriodFieldAccess,
  workScheduleByPeriodInitialValueAccess,
  workScheduleByPeriodFields,
  workScheduleByPeriodFormModes,
  workerFilterFieldNames,
} from './CreateNewWorkScheduleByPeriod.constants';

const ignoredDirtyFields = Object.values(workerFilterFieldNames);
const getPeriodMonth = (periodMonths = []) =>
  periodMonths?.[0] || periodMonths?.[1];

const CreateNewWorkScheduleByPeriodNode = ({
  companyId,
  parentScheduleId,
  parentScheduleName,
  mode = workScheduleByPeriodFormModes.create,
  schedule,
  fieldAccessConfig = workScheduleByPeriodFieldAccess,
  form,
  onCancel,
  onDraftCreate,
  onDirtyChange,
  isSubmitting,
}) => {
  const { validateFields } = form;
  const serviceSetsSectionRef = useRef(null);
  const initialValues = useMemo(
    () => getWorkScheduleByPeriodInitialValues(schedule),
    [schedule]
  );
  const canEditField = useCallback(
    (field) =>
      isWorkScheduleByPeriodFieldEditable({
        mode,
        field,
        fieldAccessConfig,
      }),
    [fieldAccessConfig, mode]
  );
  const canUseInitialValue = useCallback(
    (field) =>
      shouldUseWorkScheduleByPeriodFieldInitialValue({
        mode,
        field,
        initialValueAccessConfig: workScheduleByPeriodInitialValueAccess,
      }),
    [mode]
  );
  const periodCheck = useWorkSchedulePeriodCheck({
    enabled: mode !== workScheduleByPeriodFormModes.edit,
    initialMonth: canUseInitialValue(workScheduleByPeriodFields.period)
      ? getPeriodMonth(initialValues.periodMonths)
      : undefined,
    workScheduleId: parentScheduleId,
  });
  const addressBlock = useAddressBlock({
    companyId,
    form,
    initialValues,
    onDirtyChange,
  });
  const entities = useScheduleEntities({
    companyId,
    form,
    initialValues,
    onDirtyChange,
  });
  const canEditWorkers = canEditField(workScheduleByPeriodFields.workers);
  const canEditServiceSets = canEditField(
    workScheduleByPeriodFields.serviceSets
  );

  const showRequiredWarning = (content) => {
    Modal.warning({
      title: 'Заполните обязательные данные',
      content,
    });
  };

  const getWorkIntervalsPayload = (workerIds = []) => {
    if (
      mode === workScheduleByPeriodFormModes.duplicate &&
      !schedule?.isDraft
    ) {
      return {};
    }

    const workerIdSet = new Set(workerIds.map((workerId) => String(workerId)));

    return Object.entries(initialValues.workIntervals || {}).reduce(
      (workIntervals, [workerId, intervalsByDate]) => {
        if (workerIdSet.has(String(workerId))) {
          const workerIntervals = Object.entries(intervalsByDate || {}).reduce(
            (datesAcc, [date, interval]) => {
              datesAcc[date] = { ...(interval || {}) };

              return datesAcc;
            },
            {}
          );

          if (Object.keys(workerIntervals).length) {
            workIntervals[String(workerId)] = workerIntervals;
          }
        }

        return workIntervals;
      },
      {}
    );
  };

  const getWorkersPayload = () => {
    if (mode === workScheduleByPeriodFormModes.edit) {
      return entities.workers.selectedWorkers;
    }

    return entities.workers.selectedWorkers.map((worker) => ({
      ...worker,
      workingHours: 0,
    }));
  };

  const getServiceSetsPayload = (committedServiceSet) => {
    if (!committedServiceSet) {
      return {
        serviceSetIds: entities.serviceSetIds,
        serviceSets: entities.serviceSets.selectedItems,
      };
    }

    const committedServiceSetKey = String(committedServiceSet.id);
    const serviceSetIds = [...entities.serviceSetIds];

    if (
      !serviceSetIds.some(
        (serviceSetId) => String(serviceSetId) === committedServiceSetKey
      )
    ) {
      serviceSetIds.push(committedServiceSet.id);
    }

    return {
      serviceSetIds,
      serviceSets: [
        committedServiceSet,
        ...entities.serviceSets.selectedItems.filter(
          (serviceSet) => String(serviceSet.id) !== committedServiceSetKey
        ),
      ],
    };
  };

  const getBasePayload = (
    values,
    {
      workerIds = entities.workerIds,
      serviceSetIds = entities.serviceSetIds,
      serviceSets = entities.serviceSets.selectedItems,
    } = {}
  ) => {
    const scheduleMonth = getPeriodMonth(values.periodMonths);
    const addressPayload = addressBlock.getPayload(values);
    const cityIds = Array.isArray(addressPayload.cityIds)
      ? addressPayload.cityIds
      : [];
    const streetIds = Array.isArray(addressPayload.streetIds)
      ? addressPayload.streetIds
      : [];
    const houseIds = Array.isArray(addressPayload.houseIds)
      ? addressPayload.houseIds
      : [];
    const cityRegionIds = Array.isArray(addressPayload.cityRegionIds)
      ? addressPayload.cityRegionIds
      : [];
    const selectedCityId =
      addressPayload.cityId ?? (cityIds.length === 1 ? cityIds[0] : undefined);
    const cityId =
      mode === workScheduleByPeriodFormModes.edit
        ? initialValues.cityId ?? selectedCityId
        : selectedCityId;
    const workers = getWorkersPayload();

    return {
      companyId,
      parentScheduleId,
      allCompanyAddresses: Boolean(addressPayload.allCompanyAddresses),
      period: {
        startMonth: scheduleMonth?.format('MM.YYYY'),
        endMonth: scheduleMonth?.format('MM.YYYY'),
      },
      address: {
        ...addressPayload,
        cityId,
        cityIds,
        streetIds,
        houseIds,
      },
      cityId,
      cityIds,
      cityRegionId: cityRegionIds[0],
      cityRegionIds,
      streetIds,
      houseIds,
      workerIds,
      serviceSetIds,
      workers,
      performers: workers,
      serviceSets,
      quantityOfWorkers: workerIds.length,
      workIntervals: getWorkIntervalsPayload(workerIds),
    };
  };

  const getSchedulePayload = (values, payloadOverrides) => {
    const payload = {
      ...getBasePayload(values, payloadOverrides),
      _draftMode: mode,
    };

    if (
      mode === workScheduleByPeriodFormModes.edit &&
      schedule?.id !== undefined &&
      schedule?.id !== null
    ) {
      payload.id = schedule.id;
      payload.periodId = schedule.periodId ?? schedule.id;
      payload.workSchedulePeriodId = schedule.periodId ?? schedule.id;
      payload.sourceScheduleId = schedule.id;
    }

    if (
      mode === workScheduleByPeriodFormModes.duplicate &&
      schedule?.id !== undefined &&
      schedule?.id !== null
    ) {
      payload.sourceScheduleId = schedule.isDraft
        ? schedule.sourceScheduleId
        : schedule.id;
    }

    return payload;
  };

  const createDraftPayload = (values, payloadOverrides) => {
    const payload = getSchedulePayload(values, payloadOverrides);

    return typeof onDraftCreate === 'function'
      ? onDraftCreate(payload)
      : payload;
  };

  const handleDeleteWorker = (workerId) => {
    entities.workers.onDelete(workerId);
  };

  const handleDeleteWorkers = (workerIds = []) => {
    entities.workers.onDeleteMany(workerIds);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (periodCheck.isChecking || periodCheck.isUnavailable) {
      Modal.warning({
        title: periodCheck.isChecking
          ? 'Проверяем выбранный период'
          : 'Невозможно создать график по периоду',
        content: periodCheck.isChecking
          ? 'Дождитесь завершения проверки выбранного месяца.'
          : periodCheck.message,
      });
      return;
    }

    if (entities.serviceSets.isCreateMode || entities.serviceSets.isEditMode) {
      return;
    }

    let serviceSetCommit = {
      success: true,
    };

    try {
      serviceSetCommit = await Promise.resolve(
        serviceSetsSectionRef.current?.commitPendingChanges?.() || {
          success: true,
        }
      );
    } catch (_error) {
      serviceSetCommit = {
        success: false,
      };
    }

    if (!serviceSetCommit.success) {
      return;
    }

    validateFields((errors, values) => {
      if (errors) {
        return;
      }

      const serviceSetsPayload = getServiceSetsPayload(
        serviceSetCommit.serviceSet
      );

      if (
        canEditField(workScheduleByPeriodFields.address) &&
        !hasAddressValues({
          allCompanyAddresses: addressBlock.allCompanyAddresses,
          values,
        })
      ) {
        showRequiredWarning('Выберите город, улицу и дом');
        return;
      }

      if (canEditWorkers && !entities.workerIds.length) {
        showRequiredWarning('Добавьте минимум одного исполнителя');
        return;
      }

      if (!serviceSetsPayload.serviceSetIds.length) {
        showRequiredWarning('Выберите минимум один набор услуг');
        return;
      }

      createDraftPayload(values, serviceSetsPayload);
    });
  };

  return (
    <Form className={styles.root} onSubmit={handleSubmit}>
      {mode === workScheduleByPeriodFormModes.create &&
        Boolean(parentScheduleName) && (
          <div className={styles.parentSchedule}>
            <span className={styles.parentScheduleLabel}>График</span>
            <h2 className={styles.parentScheduleName}>{parentScheduleName}</h2>
          </div>
        )}
      <div className={styles.blocksRow}>
        <MainAddressSection
          form={form}
          initialValues={initialValues}
          canEditField={canEditField}
          canUseInitialValue={canUseInitialValue}
          allCompanyAddresses={addressBlock.allCompanyAddresses}
          cityOptions={addressBlock.cityOptions}
          cityRegionOptions={addressBlock.cityRegionOptions}
          cityRegionDisabled={addressBlock.cityRegionDisabled}
          cityRegionDisabledHint={addressBlock.cityRegionDisabledHint}
          cityDisabled={addressBlock.cityDisabled}
          cityDisabledHint={addressBlock.cityDisabledHint}
          streetOptions={addressBlock.streetOptions}
          streetDisabled={addressBlock.streetDisabled}
          streetDisabledHint={addressBlock.streetDisabledHint}
          houseOptions={addressBlock.houseOptions}
          houseDisabled={addressBlock.houseDisabled}
          houseDisabledHint={addressBlock.houseDisabledHint}
          citiesLoading={addressBlock.citiesLoading}
          cityRegionsLoading={addressBlock.cityRegionsLoading}
          streetsLoading={addressBlock.streetsLoading}
          housesLoading={addressBlock.housesLoading}
          periodCheckLoading={periodCheck.isChecking}
          periodCheckMessage={periodCheck.message}
          onAllCompanyAddressesChange={addressBlock.onAllCompanyAddressesChange}
          onPeriodChange={periodCheck.onMonthChange}
        />
        <WorkersSection
          form={form}
          isEditable={canEditWorkers}
          initialSelectedWorkerIds={initialValues.workerIds}
          selectedWorkers={entities.workers.selectedWorkers}
          workerOptions={entities.workers.options}
          workerSpecialityOptions={entities.workers.specialityOptions}
          workerRoleOptions={entities.workers.roleOptions}
          workersLoading={entities.workers.loading}
          onAdd={entities.workers.onAdd}
          onAddMany={entities.workers.onAddMany}
          onDelete={handleDeleteWorker}
          onDeleteMany={handleDeleteWorkers}
        />
        <ServiceSetsSection
          ref={serviceSetsSectionRef}
          form={form}
          isEditable={canEditServiceSets}
          initialSelectedServiceSetIds={initialValues.serviceSetIds}
          mode={entities.serviceSets.mode}
          isCreateMode={entities.serviceSets.isCreateMode}
          isEditMode={entities.serviceSets.isEditMode}
          isDeleteMode={entities.serviceSets.isDeleteMode}
          selectedServiceSetIds={entities.serviceSets.selectedIds}
          selectedServiceSets={entities.serviceSets.selectedItems}
          serviceSets={entities.serviceSets.items}
          serviceSetOptions={entities.serviceSets.options}
          serviceOptions={entities.serviceSets.serviceOptions}
          serviceReasonGroups={entities.serviceSets.serviceReasonGroups}
          servicesLoading={entities.serviceSets.servicesLoading}
          serviceDetailsLoading={entities.serviceSets.serviceDetailsLoading}
          reasonsLoading={entities.serviceSets.reasonsLoading}
          createLoading={entities.serviceSets.createLoading}
          updateLoading={entities.serviceSets.updateLoading}
          deleteLoading={entities.serviceSets.deleteLoading}
          getReasonKeysFromServiceSet={
            entities.serviceSets.getReasonKeysFromServiceSet
          }
          onChange={entities.serviceSets.onChange}
          onCreate={entities.serviceSets.onCreate}
          onUpdate={entities.serviceSets.onUpdate}
          onDelete={entities.serviceSets.onDelete}
          onModeChange={entities.serviceSets.onModeChange}
        />
      </div>
      <FormFooter
        mode={mode}
        submitDisabled={
          isSubmitting ||
          periodCheck.isChecking ||
          periodCheck.isUnavailable ||
          entities.serviceSets.isCreateMode ||
          entities.serviceSets.isEditMode
        }
        onCancel={onCancel}
      />
    </Form>
  );
};

export const CreateNewWorkScheduleByPeriod = Form.create({
  name: 'create_new_work_schedule_by_period',
  onValuesChange: (props, _changedValues, allValues) => {
    if (typeof props.onDirtyChange === 'function') {
      props.onDirtyChange(hasFormValues(allValues, ignoredDirtyFields));
    }
  },
})(CreateNewWorkScheduleByPeriodNode);
